import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Task } from '@/types/notepad';
import {
  useToggleTaskDone,
  useSnoozeTask,
  useDeleteTask,
  useSetTaskReminder,
  useRestoreTask,
} from '@/hooks/useMyTasks';
import { useDeleteConfirmMode } from '@/hooks/useNotepadPreferences';
import { useNotepadEditor } from './useNotepadEditorState';
import { InlineDeleteConfirm } from './InlineDeleteConfirm';
import { notepadToast } from '@/lib/toast/notepadToast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Check,
  Clock,
  MoreVertical,
  Pencil,
  FileText,
  Trash2,
  Sun,
  Bell,
  BellOff,
} from 'lucide-react';
import { formatDueDate } from './parseTaskInput';
import { addMinutes, addDays, setHours, setMinutes as setMin, startOfDay } from 'date-fns';
import { DEFAULT_REMINDER_HOUR, DEFAULT_REMINDER_MINUTE } from '@/constants/notepad';
import { getPriorityStyles, getDueDateStyles, getCardBorderStyles } from '@/lib/taskStyles';

interface TaskCardProps {
  task: Task;
}

/**
 * Task card component
 * Shows title, status, priority, due date
 * Actions: Done, Edit, Snooze, Open note, Delete
 */
export function TaskCard({ task }: TaskCardProps) {
  const { t } = useTranslation(['notepad', 'common']);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { isUndoOnlyMode } = useDeleteConfirmMode();
  const { openEdit } = useNotepadEditor();
  const toggleDone = useToggleTaskDone();
  const snooze = useSnoozeTask();
  const deleteTask = useDeleteTask();
  const restoreTask = useRestoreTask();
  const setReminder = useSetTaskReminder();

  const handleEdit = useCallback(() => {
    openEdit('task', task.id);
  }, [openEdit, task.id]);

  const handleToggleDone = useCallback(() => {
    toggleDone.mutate(task.id);
  }, [toggleDone, task.id]);

  const handleSnooze = useCallback(
    (minutes: number) => {
      snooze.mutate({ taskId: task.id, minutes });
    },
    [snooze, task.id]
  );

  const handleSnoozeTomorrow = () => {
    // Snooze to tomorrow at default reminder time
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const tomorrow9am = setMin(setHours(tomorrow, DEFAULT_REMINDER_HOUR), DEFAULT_REMINDER_MINUTE);
    const now = new Date();
    const minutesUntil = Math.floor((tomorrow9am.getTime() - now.getTime()) / 60000);
    snooze.mutate({ taskId: task.id, minutes: minutesUntil });
  };

  const handleSetReminder = useCallback(
    (minutes: number) => {
      const remindAt = addMinutes(new Date(), minutes);
      setReminder.mutate({ taskId: task.id, remindAt });
    },
    [setReminder, task.id]
  );

  const handleSetReminderTomorrow = () => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const tomorrow9am = setMin(setHours(tomorrow, DEFAULT_REMINDER_HOUR), DEFAULT_REMINDER_MINUTE);
    setReminder.mutate({ taskId: task.id, remindAt: tomorrow9am });
  };

  const handleClearReminder = () => {
    setReminder.mutate({ taskId: task.id, remindAt: null });
  };

  const handleDelete = () => {
    if (isUndoOnlyMode) {
      // Undo-only mode: immediately delete with toast
      handleConfirmDelete();
    } else {
      // Inline mode: show confirmation
      setDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    setDeleteConfirmOpen(false);

    deleteTask.mutate(task.id, {
      onSuccess: () => {
        // Show undo toast with 5 second duration
        notepadToast.taskDeleted(task.title, () => {
          // Restore task by clearing deletedAt
          restoreTask.mutate(task.id, {
            onSuccess: () => {
              setIsDeleting(false);
            },
          });
        });
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  // Handle click outside to close confirmation
  useEffect(() => {
    if (!deleteConfirmOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setDeleteConfirmOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [deleteConfirmOpen]);

  // Status badge
  const statusVariant = task.status === 'blocked' ? 'destructive' : 'secondary';
  const statusLabel =
    task.status === 'done'
      ? 'Completed'
      : task.status === 'in_progress'
        ? 'In Progress'
        : task.status === 'blocked'
          ? 'Blocked'
          : 'Open';

  // Get styling based on priority and due date
  const priorityStyle = getPriorityStyles(task.priority);
  const dueDateStyle = getDueDateStyles(task.dueAt);
  const cardBorderStyle = getCardBorderStyles(task.priority, task.dueAt);

  // Don't render if deleting (optimistic removal)
  if (isDeleting) return null;

  return (
    <motion.div
      ref={cardRef}
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.18 },
        height: { duration: 0.18 },
      }}
      className={`group relative rounded-xl border transition-all duration-200 ${cardBorderStyle} ${
        deleteConfirmOpen ? 'isolate z-[35] shadow-md' : 'hover:bg-muted/50 hover:shadow-sm'
      }`}
    >
      {/* Show inline confirmation or normal content */}
      <AnimatePresence mode="wait">
        {deleteConfirmOpen ? (
          <InlineDeleteConfirm
            key="confirm"
            isOpen={deleteConfirmOpen}
            title={task.title}
            type="task"
            onConfirm={handleConfirmDelete}
            onCancel={() => {
              setDeleteConfirmOpen(false);
              menuButtonRef.current?.focus();
            }}
          />
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-start gap-3 p-4"
          >
            {/* Done checkbox */}
            <motion.button
              onClick={handleToggleDone}
              whileTap={{ scale: 0.9 }}
              className={`mt-0.5 flex-shrink-0 h-6 w-6 rounded border-2 transition-all duration-200 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                task.status === 'done'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground hover:border-primary'
              }`}
              aria-label={
                task.status === 'done'
                  ? t('notepad:actions.markAsOpen')
                  : t('notepad:actions.markAsDone')
              }
            >
              <motion.div
                initial={false}
                animate={{
                  scale: task.status === 'done' ? 1 : 0,
                  opacity: task.status === 'done' ? 1 : 0,
                }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Check className="h-4 w-4" />
              </motion.div>
            </motion.button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <motion.h4
                  className="text-sm font-medium leading-tight relative"
                  animate={{
                    opacity: task.status === 'done' ? 0.6 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {task.title}
                  {/* Strikethrough animation */}
                  <motion.div
                    className="absolute inset-0 flex items-center"
                    initial={false}
                    animate={{
                      scaleX: task.status === 'done' ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{ originX: 0 }}
                  >
                    <div className="w-full h-[1.5px] bg-current" />
                  </motion.div>
                </motion.h4>

                {/* Actions menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      ref={menuButtonRef}
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      aria-label={t('accessibility.taskActions')}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleToggleDone}>
                      <Check className="mr-2 h-4 w-4" />
                      {task.status === 'done'
                        ? t('notepad:actions.markAsOpen')
                        : t('notepad:actions.markAsDone')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      {t('notepad:actions.edit')}
                    </DropdownMenuItem>
                    {task.noteId && (
                      <DropdownMenuItem disabled>
                        <FileText className="mr-2 h-4 w-4" />
                        {t('notepad:actions.openNote')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleSnooze(15)}>
                      <Clock className="mr-2 h-4 w-4" />
                      {t('notepad:actions.snooze15min')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSnooze(60)}>
                      <Clock className="mr-2 h-4 w-4" />
                      {t('notepad:actions.snooze1hour')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSnoozeTomorrow}>
                      <Sun className="mr-2 h-4 w-4" />
                      {t('notepad:actions.tomorrowMorning')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* Remind me options */}
                    <DropdownMenuItem onClick={() => handleSetReminder(15)}>
                      <Bell className="mr-2 h-4 w-4" />
                      {t('notepad:actions.remindIn15min')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSetReminder(60)}>
                      <Bell className="mr-2 h-4 w-4" />
                      {t('notepad:actions.remindIn1hour')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSetReminder(180)}>
                      <Bell className="mr-2 h-4 w-4" />
                      {t('notepad:actions.remindIn3hours')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSetReminderTomorrow}>
                      <Bell className="mr-2 h-4 w-4" />
                      {t('notepad:actions.remindTomorrow')}
                    </DropdownMenuItem>
                    {task.remindAt && (
                      <DropdownMenuItem onClick={handleClearReminder}>
                        <BellOff className="mr-2 h-4 w-4" />
                        {t('notepad:actions.clearReminder')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('notepad:actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {/* Status */}
                <AnimatePresence mode="wait">
                  {task.status !== 'open' && (
                    <motion.div
                      key={task.status}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Badge variant={statusVariant} className="text-xs">
                        {statusLabel}
                      </Badge>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Priority */}
                {priorityStyle && (
                  <Badge className={`text-xs border ${priorityStyle.className}`}>
                    {priorityStyle.label}
                  </Badge>
                )}

                {/* Due date */}
                {task.dueAt && (
                  <span
                    className={`text-xs flex items-center gap-1 ${
                      dueDateStyle?.className || 'text-muted-foreground'
                    }`}
                  >
                    <Clock className="h-3 w-3" />
                    {formatDueDate(task.dueAt.toDate())}
                  </span>
                )}

                {/* Tags */}
                {task.tags?.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
